import React, { useState, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import './ExportCSV.css';
import { set }  from 'react-ga';

const ExportCSVOptions = ({ job }) => {
    const csvLink = React.useRef();
    const [showExportOptionsModal, setShowExportOptionsModal] = useState(false);
    const [csvData, setCsvData] = useState(null);
    const [exportName, setExportName] = useState('');
    const [shouldDownload, setShouldDownload] = useState(false);
    const [taskList, setTaskList] = useState([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [loadingError, setLoadingError] = useState(null);

    const toggleExportOptionsModal = () => {
        setShowExportOptionsModal(!showExportOptionsModal);
    };

    useEffect(() => {
        if (shouldDownload && csvData && csvLink.current) {
            csvLink.current.link.click();
            setShouldDownload(false);
        }
    }, [shouldDownload, csvData]);

    useEffect(() => {
        const checkDataLoaded = () => {
            try {
                if (!job) {
                    setLoadingError('Job data not loaded');
                    return false;
                }
                
                if (!job.job_number) {
                    setLoadingError('Job number missing');
                    return false;
                }
                
                if (!Array.isArray(job.tickets)) {
                    setLoadingError('Tickets data not properly loaded');
                    return false;
                }
                
                if (job.tickets.length === 0) {
                    setLoadingError('No tickets in job');
                    return false;
                }
                
                const allTicketsLoaded = job.tickets.every((ticket, index) => {
                    if (!ticket) {
                        setLoadingError(`Ticket at index ${index} is null or undefined`);
                        return false;
                    }
                    
                    if (!ticket.pole_name) {
                        setLoadingError(`Ticket ${index}: Pole name missing`);
                        return false;
                    }
                    
                    if (!ticket.line_name) {
                        setLoadingError(`Ticket ${index}: Line name missing`);
                        return false;
                    }

                    if (!Array.isArray(ticket.tasks)) {
                        setLoadingError(`Ticket ${index}: Tasks not properly loaded`);
                        return false;
                    }
                    
                    if (!Array.isArray(ticket.notes)) {
                        setLoadingError(`Ticket ${index}: Notes not properly loaded`);
                        return false;
                    }

                    if (!Array.isArray(ticket.work_orders)) {
                        setLoadingError(`Ticket ${index}: Work orders not properly loaded`);
                        return false;
                    }

                    if (!ticket.pole || typeof ticket.pole !== 'object') {
                        setLoadingError(`Ticket ${index}: Pole data not properly loaded`);
                        return false;
                    }

                    if (ticket.pole.geo_lat === undefined || ticket.pole.geo_lon === undefined) {
                        setLoadingError(`Ticket ${index}: Geo data not properly loaded`);
                        return false;
                    }
                    
                    return true;
                });
                
                if (!allTicketsLoaded) {
                    return false;
                }
                

                setLoadingError(null);
                return true;
                
            } catch (error) {
                setLoadingError(`Error checking data: ${error.message}`);
                return false;
            }
        };

        const isLoaded = checkDataLoaded();
        setIsDataLoaded(isLoaded);
    }, [job]);

    const getFormattedDate = (val) => {
        const d = val.toISOString().split('T');
        const date = d[0];
        const time = d[1].split('.')[0];
        return `${date} ${time}`;
    };

    const exportOption1 = (job, taskList, buttonName) => {
        const poleNumber = job.job_number;
        setExportName(`${poleNumber}_${buttonName}`);

        const data = job.tickets.map(ticket => {
            const taskCodes = ticket.tasks?.map(task => task.ticket_task_id).join('\n');
            const taskDescriptions = ticket.tasks?.map(task => task.description).join('\n');
            const init_qty = ticket.tasks?.map(task => task.init_qty).join('\n');
            const notesContent = ticket.notes?.map(note => note.note_text).join('\n');
            const completedQuantity = ticket.tasks?.map(task => task.additional_qty).join('\n');

            var complete_date = null;
            if (ticket.actual_completion_date != null) {
                complete_date = getFormattedDate(new Date(ticket.actual_completion_date));
            }

            return {
                poleNumber: ticket.pole_name,
                line: ticket.line_name,
                workOrderNumbers: ticket.work_orders.join('\n'),
                notes: notesContent,
                address: ticket.street_address,
                taskCodes: taskCodes,
                taskDescriptions: taskDescriptions,
                init_qty: init_qty,
                completedQuantity: completedQuantity,
                longitude: ticket.pole.geo_lon,
                latitude: ticket.pole.geo_lat,
                utility: job.selected_utility_name,
                dueDate: job.due_date,
                wfCrew: ticket.assign_to_name,
                gps: ticket.pole_lat,
                dateCompleted: complete_date,
            };
        });

        const headers = [
            { label: "Pole #", key: "poleNumber" },
            { label: "Line #", key: "line" },
            { label: "Work Order #", key: "workOrderNumbers" },
            { label: "Address", key: "address" },
            { label: "Unit/Task Code", key: "taskCodes" },
            { label: "Unit Description", key: "taskDescriptions" },
            { label: "Quantity Bid", key: "init_qty" },
            { label: "Completed Quantity", key: "completedQuantity" },
            { label: "Latitude", key: "latitude" },
            { label: "Longitude", key: "longitude" },
            { label: "Notes", key: "notes" },
            { label: "Completion Date/Time", key: "dateCompleted" },
        ];
        setCsvData({ data, headers });
        setShouldDownload(true);
        toggleExportOptionsModal();
    };

    const exportOption2 = (job, taskList, buttonName) => {
        const poleNumber = job.job_number;
        setExportName(`${poleNumber}_${buttonName}`);

        const data = job.tickets.map(ticket => {
            const taskCodes = ticket.tasks?.map(task => task.ticket_task_id).join(', ');
            const taskDescriptions = ticket.tasks?.map(task => task.description).join(', ');
            const init_qty = ticket.tasks?.map(task => task.init_qty).join(', ');
            const notesContent = ticket.notes?.map(note => note.note_text).join(', ');

            var complete_date = null;
            if (ticket.actual_completion_date != null) {
                complete_date = getFormattedDate(new Date(ticket.actual_completion_date));
            }

            return {
                jobNumber: job.job_number,
                poleNumber: ticket.pole_name,
                line: ticket.line_name,
                workOrderNumbers: ticket.work_orders.join('\n'),
                notes: notesContent,
                address: ticket.street_address,
                taskCodes: taskCodes,
                taskDescriptions: taskDescriptions,
                init_qty: init_qty,
                utility: job.selected_utility_name,
                dueDate: job.due_date,
                wfCrew: ticket.assign_to_name,
                gps: ticket.pole_lat,
                dateCompleted: complete_date,
            };
        });

        const headers = [
            { label: "Job Name", key: "jobNumber" },
            { label: "Pole #", key: "poleNumber" },
            { label: "Line #", key: "line" },
            { label: "Work Order #", key: "workOrderNumbers" },
            { label: "Address", key: "address" },
            { label: "Unit/Task Code", key: "taskCodes" },
            { label: "Unit Description", key: "taskDescriptions" },
            { label: "Quantity Bid", key: "init_qty" },
            { label: "Notes", key: "notes" },
            { label: "Completion Date/Time", key: "dateCompleted" },
        ];
        setCsvData({ data, headers });
        setShouldDownload(true);
        toggleExportOptionsModal();
    };

    return (
        <>
            <button 
                type="button" 
                className="btn btn-t text-center btn-exp-job" 
                onClick={toggleExportOptionsModal} 
                disabled={!isDataLoaded}
                title={loadingError || "Export Job Info"}
            >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
                    <path d="M11.3705 10.384C10.7848 10.9698 10.7848 11.9195 11.3705 12.5053L11.4373 12.5721C12.0231 13.1579 12.9728 13.1579 13.5586 12.5721L16.6736 9.45707C16.8178 9.31284 16.9266 9.14656 16.9998 8.96907C16.9999 8.97936 17 8.98967 17 9V17C17 18.1046 16.1046 19 15 19H7C5.89543 19 5 18.1046 5 17V9C5 7.89543 5.89543 7 7 7L14.8511 7C14.7195 7.06882 14.5961 7.15848 14.4855 7.269L11.3705 10.384Z" fill="#212529"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M19.2175 4.78249C19.4128 4.97776 19.4128 5.29434 19.2175 5.4896L12.8536 11.8536C12.6583 12.0488 12.3417 12.0488 12.1464 11.8536C11.9512 11.6583 11.9512 11.3417 12.1464 11.1465L18.5104 4.78249C18.7057 4.58723 19.0223 4.58723 19.2175 4.78249Z" fill="#212529"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14 4.5C14 4.22386 14.2239 4 14.5 4H19.5C19.7761 4 20 4.22386 20 4.5V9.5C20 9.77614 19.7761 10 19.5 10C19.2239 10 19 9.77614 19 9.5V5H14.5C14.2239 5 14 4.77614 14 4.5Z" fill="#212529"/>
                </svg>
                Export Job Info
            </button>

            {showExportOptionsModal && (
                <div className="modal fade show modalContain" style={{ display: "block" }} onClick={toggleExportOptionsModal}>
                    <div className="modal-dialog" role="document" onClick={e => e.stopPropagation()}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <div className="modal-title">
                                    <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
                                        <path d="M11.3705 10.384C10.7848 10.9698 10.7848 11.9195 11.3705 12.5053L11.4373 12.5721C12.0231 13.1579 12.9728 13.1579 13.5586 12.5721L16.6736 9.45707C16.8178 9.31284 16.9266 9.14656 16.9998 8.96907C16.9999 8.97936 17 8.98967 17 9V17C17 18.1046 16.1046 19 15 19H7C5.89543 19 5 18.1046 5 17V9C5 7.89543 5.89543 7 7 7L14.8511 7C14.7195 7.06882 14.5961 7.15848 14.4855 7.269L11.3705 10.384Z" fill="#ffffff"/>
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.2175 4.78249C19.4128 4.97776 19.4128 5.29434 19.2175 5.4896L12.8536 11.8536C12.6583 12.0488 12.3417 12.0488 12.1464 11.8536C11.9512 11.6583 11.9512 11.3417 12.1464 11.1465L18.5104 4.78249C18.7057 4.58723 19.0223 4.58723 19.2175 4.78249Z" fill="#ffffff"/>
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M14 4.5C14 4.22386 14.2239 4 14.5 4H19.5C19.7761 4 20 4.22386 20 4.5V9.5C20 9.77614 19.7761 10 19.5 10C19.2239 10 19 9.77614 19 9.5V5H14.5C14.2239 5 14 4.77614 14 4.5Z" fill="#ffffff"/>
                                    </svg>
                                    Select an export option
                                </div>
                                <button type="button" className="close" onClick={toggleExportOptionsModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <button type="button" className="btn btn-t text-center btn-exp" onClick={() => exportOption1(job, taskList, 'Export-Option1')}>Export Option 1 ( Priority items )</button>
                                <button type="button" className="btn btn-t text-center btn-exp" onClick={() => exportOption2(job, taskList, 'Export-Option2')}>Export Option 2 ( All  items )</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {csvData && (
                <CSVLink
                    data={csvData.data}
                    headers={csvData.headers}
                    filename={`${exportName}`}
                    style={{ display: 'none' }}
                    ref={link => csvLink.current = link}
                />
            )}
        </>
    );
};

export default ExportCSVOptions;